import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Brain, FolderOpen, Users, BarChart } from "lucide-react";
import { useProjects } from "@/hooks/useProjects";
import { supabase } from "@/integrations/supabase/client";

const MetricsGrid = () => {
  const { projects, isLoading: projectsLoading } = useProjects();
  const [stats, setStats] = useState({ teamMembers: 1, insights: 0, runs: 0, successRate: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const [insightsRes, runsRes, teamRes] = await Promise.all([
        supabase.from('user_ai_interactions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('workflow_runs').select('id, status').eq('user_id', user.id),
        supabase.from('team_members').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
      ]);

      const runs = runsRes.data || [];
      const successful = runs.filter((r: any) => r.status === 'completed' || r.status === 'success').length;
      const successRate = runs.length ? Math.round((successful / runs.length) * 100) : 0;

      setStats({
        teamMembers: (teamRes.count ?? 0) + 1,
        insights: insightsRes.count || 0,
        runs: runs.length,
        successRate,
      });
      setLoading(false);
    };

    fetchStats();
  }, []);

  const isLoading = projectsLoading || loading;

  const metrics = [
    {
      title: "Your Projects",
      value: isLoading ? "..." : projects.length,
      icon: FolderOpen,
      change: "total projects",
    },
    {
      title: "AI Insights",
      value: isLoading ? "..." : stats.insights,
      icon: Brain,
      change: "generated",
    },
    {
      title: "Team Members",
      value: isLoading ? "..." : stats.teamMembers,
      icon: Users,
      change: "on your team",
    },
    {
      title: "Workflow Success",
      value: isLoading ? "..." : stats.runs === 0 ? "—" : `${stats.successRate}%`,
      icon: BarChart,
      change: stats.runs === 0 ? "no runs yet" : `${stats.runs} runs`,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
      {metrics.map((metric) => (
        <Card key={metric.title} className="p-4 md:p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{metric.title}</p>
              <h3 className="text-xl md:text-2xl font-bold mt-1 md:mt-2">{metric.value}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {metric.change}
              </p>
            </div>
            <metric.icon className="w-5 h-5 text-primary" />
          </div>
        </Card>
      ))}
    </div>
  );
};

export default MetricsGrid;
